import { useMemo, useState } from "react";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { adminClient } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { computeStats, isSetter, positionLabel, sortRoster, TEAM_NAME } from "@/lib/dvStats";
import SetTabs from "./SetTabs";

export default function StatsView({ match, events, readOnly = false }) {
  const { t, lang } = useT();
  const [scope, setScope] = useState("total");
  const [exporting, setExporting] = useState(false);
  const stats = useMemo(
    () => computeStats(match.roster, events, scope === "total" ? null : scope),
    [match.roster, events, scope]
  );

  const exportFile = async (kind) => {
    setExporting(kind);
    try {
      const path = kind === "pdf" ? "export.pdf" : "export";
      const { data } = await adminClient.get(`/datavolley/matches/${match.id}/${path}`, {
        params: { scope },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `datavolley_${match.opponent}_${match.date}_${scope}.${kind}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const cards = [
    { label: t("dv_rec_pct"), value: `${stats.team.receptionPct}%`, sub: `${stats.team.reception_excellent}/${stats.team.receptionTotal}`, cls: "bg-emerald-500", id: "rec" },
    { label: t("dv_att_pct"), value: `${stats.team.attackPct}%`, sub: `${stats.team.attack_point}/${stats.team.attackTotal}`, cls: "bg-rose-500", id: "att" },
    { label: t("dv_aces"), value: stats.team.aces, sub: `${stats.team.serveTotal} ${t("dv_serve").toLowerCase()}`, cls: "bg-sky-600", id: "aces" },
    { label: t("dv_blocks"), value: stats.team.blocks, sub: "", cls: "bg-amber-400 text-slate-900", id: "blocks" },
    { label: t("dv_set_pct"), value: `${stats.team.setPct}%`, sub: `${stats.team.set_excellent}/${stats.team.setTotal}`, cls: "bg-emerald-700", id: "set" },
    { label: t("dv_errors"), value: stats.team.errorsTotal, sub: `${stats.team.reception_poor}+${stats.team.serve_error}+${stats.team.attack_error}+${stats.team.set_error}`, cls: "bg-slate-700", id: "errors" },
  ];

  return (
    <div className="flex flex-col gap-4" data-testid="dv-stats-view">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-slate-900 px-4 py-3">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">{t("dv_stats")}</p>
          <p className="font-heading font-black text-lg text-white truncate" data-testid="dv-stats-opponent">
            <span className="text-slate-400">{TEAM_NAME} vs</span> {match.opponent} · {match.date}
          </p>
          {match.set_scores && Object.keys(match.set_scores).length > 0 && (
            <p className="text-xs text-slate-300 mt-0.5 flex flex-wrap gap-x-3" data-testid="dv-stats-set-scores">
              {[1, 2, 3, 4, 5].filter((n) => match.set_scores[String(n)]).map((n) => (
                <span key={n}><span className="text-slate-500">S{n}</span> {match.set_scores[String(n)][0]}-{match.set_scores[String(n)][1]}</span>
              ))}
            </p>
          )}
        </div>
        <SetTabs value={scope} onChange={setScope} />
        {!readOnly && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => exportFile("csv")}
            disabled={!!exporting}
            data-testid="dv-export-btn"
            className="dv-btn h-11 px-4 rounded-xl bg-white text-slate-900 font-bold flex items-center gap-2 disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> {t("dv_export_csv")}
          </button>
          <button
            type="button"
            onClick={() => exportFile("pdf")}
            disabled={!!exporting}
            data-testid="dv-export-pdf-btn"
            className="dv-btn h-11 px-4 rounded-xl bg-sunset text-white font-bold flex items-center gap-2 disabled:opacity-50"
          >
            <FileText className="h-4 w-4" /> {exporting === "pdf" ? "…" : t("dv_export_pdf")}
          </button>
        </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {cards.map((c) => (
          <div key={c.id} className={`rounded-2xl p-4 text-white ${c.cls}`} data-testid={`dv-team-card-${c.id}`}>
            <p className="text-xs font-bold uppercase tracking-widest opacity-90">{t("dv_team")} · {c.label}</p>
            <p className="font-heading font-black text-4xl mt-1 leading-none">{c.value}</p>
            {c.sub && <p className="text-xs mt-1 opacity-80">{c.sub}</p>}
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-soft">
        <table className="w-full min-w-[820px] text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <th className="text-left px-4 py-3">{t("dv_player")}</th>
              <th className="px-3 py-3 bg-emerald-50 text-emerald-800">{t("dv_rec_pct")}</th>
              <th className="px-3 py-3 bg-emerald-50 text-emerald-800">{t("dv_excellent")}/{t("dv_medium")}/{t("dv_poor")}</th>
              <th className="px-3 py-3 bg-rose-50 text-rose-800">{t("dv_att_pct")}</th>
              <th className="px-3 py-3 bg-rose-50 text-rose-800">{t("dv_point")}/{t("dv_inplay")}/{t("dv_error")}</th>
              <th className="px-3 py-3 bg-sky-50 text-sky-800">{t("dv_aces")}</th>
              <th className="px-3 py-3 bg-sky-50 text-sky-800">{t("dv_ace")}/{t("dv_passed")}/{t("dv_missed")}</th>
              <th className="px-3 py-3 bg-amber-50 text-amber-800">{t("dv_blocks")}</th>
              <th className="px-3 py-3 bg-slate-100 text-slate-700">{t("dv_errors")}</th>
            </tr>
          </thead>
          <tbody>
            {sortRoster(match.roster).map((p) => {
              const s = stats.players[p.player_id];
              return (
                <tr key={p.player_id} className="border-b border-slate-100" data-testid={`dv-stats-row-${p.number}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <span className="font-heading font-black text-lg text-slate-900 w-8">#{p.number}</span>
                      <div>
                        <p className="font-bold text-slate-900 leading-tight">{p.name}</p>
                        <p className="text-[11px] text-slate-500">{positionLabel(p.position, lang)}</p>
                      </div>
                    </div>
                  </td>
                  {isSetter(p) ? (
                    <>
                      <td className="text-center font-heading font-black text-lg text-emerald-700 bg-emerald-50/60" data-testid={`dv-stats-set-${p.number}`}>{s.setPct}%</td>
                      <td className="text-center text-slate-600 tabular-nums bg-emerald-50/60" data-testid={`dv-stats-set-counts-${p.number}`}>
                        <span className="block text-[9px] font-bold text-emerald-700">{t("dv_set_label")}: {t("dv_excellent")}/{t("dv_inplay")}/{t("dv_error")}</span>
                        {s.set_excellent} / {s.set_inplay} / {s.set_error}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="text-center font-heading font-black text-lg text-emerald-700" data-testid={`dv-stats-recpct-${p.number}`}>{s.receptionPct}%</td>
                      <td className="text-center text-slate-600 tabular-nums">{s.reception_excellent} / {s.reception_medium} / {s.reception_poor}</td>
                    </>
                  )}
                  <td className="text-center font-heading font-black text-lg text-rose-700" data-testid={`dv-stats-attpct-${p.number}`}>{s.attackPct}%</td>
                  <td className="text-center text-slate-600 tabular-nums">{s.attack_point} / {s.attack_inplay} / {s.attack_error}</td>
                  <td className="text-center font-heading font-black text-lg text-sky-700" data-testid={`dv-stats-aces-${p.number}`}>{s.aces}</td>
                  <td className="text-center text-slate-600 tabular-nums">{s.serve_ace} / {s.serve_inplay} / {s.serve_error}</td>
                  <td className="text-center font-heading font-black text-lg text-amber-700" data-testid={`dv-stats-blocks-${p.number}`}>{s.blocks}</td>
                  <td className="text-center font-heading font-black text-lg text-slate-700 bg-slate-50" data-testid={`dv-stats-errors-${p.number}`}>{s.errorsTotal}</td>
                </tr>
              );
            })}
            <tr className="bg-slate-100 font-heading font-black text-slate-900" data-testid="dv-stats-team-row">
              <td className="px-4 py-3 tracking-widest text-sm">{t("dv_team_totals")}</td>
              <td className="text-center text-lg">{stats.team.receptionPct}%</td>
              <td className="text-center text-sm font-body font-semibold text-slate-600">{stats.team.reception_excellent} / {stats.team.reception_medium} / {stats.team.reception_poor}</td>
              <td className="text-center text-lg">{stats.team.attackPct}%</td>
              <td className="text-center text-sm font-body font-semibold text-slate-600">{stats.team.attack_point} / {stats.team.attack_inplay} / {stats.team.attack_error}</td>
              <td className="text-center text-lg">{stats.team.aces}</td>
              <td className="text-center text-sm font-body font-semibold text-slate-600">{stats.team.serve_ace} / {stats.team.serve_inplay} / {stats.team.serve_error}</td>
              <td className="text-center text-lg">{stats.team.blocks}</td>
              <td className="text-center text-lg" data-testid="dv-stats-team-errors">{stats.team.errorsTotal}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
